import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useRecordContext, useGetList } from 'react-admin';
import { Funeral } from '../types';
import { supabase } from '../lib/supabaseClient';
import { getToken } from '../authentication/authProvider';
import { useRefresh } from 'react-admin';
// Use the webpack entry point which bundles the worker locally — no CDN needed
// @ts-ignore — no type declarations for this entry point
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';

// These are the regex patterns from your provided code
const LABELS = {
  dateNaissance: /Date de naissance\s+([0-9]{4}-[0-9]{2}-[0-9]{2})/i,
  sexe: /Sexe\s+([A-Za-z]+)/i,
  age: /Âge\s+([0-9]+)\s*a/i,
  provinceNaissance: /Province de naissance\s+([A-Za-zÀ-ÿ' -]+)/i,
  paysNaissance: /Pays de naissance\s+([A-Za-zÀ-ÿ' -]+)/i,
  numeroAssurance: /No d'assurance maladie\s+([A-Z0-9]+)/i,
  numeroFormulaire: /No de formulaire\s+([0-9 -]+)/i,
  endroitDeces: /Endroit où le décès est survenu\s+([^\n]+)/i,
  codeLieu: /Code du lieu\s+([0-9]+)/i,
  adresseDeces: /Lieu de décès\s+[\s\S]*?Adresse\s+([^\n]+)\s+Municipalité\s+([^\n]+)\s+Code postal\s+([A-Z0-9 ]+)\s+Province\s+([^\n]+)\s+Pays\s+([^\n]+)/i,
  pere: /Nom et prénom du père\s+([^\n]+)/i,
  mere: /Nom et prénom de la mère\s+([^\n]+)/i,
  statutCivil: /Statut\s+([^\n]+)/i,
  nomDefunt: /Nom et prénom de la personne décédée:\s*([^\n]+)/i,
  dateHeureDeces: /Date et heure du décès\s+([0-9-]{10}\s+[0-9:]{8})/i,
  causea: /a\)\s*due à.*?\s*([^\n]+)/i,
  autopsie: /Y a-t-il eu autopsie\?\s+([^\n]+)/i,
  auteurNom: /Nom et prénom\s+([^\n]+)\s+Pour toutes questions/i,
  auteurQualite: /Qualité de l'auteur\s+([^\n]+)/i,
  auteurPermis: /Numéro de permis d'exercice\s+([^\n]+)/i,
  auteurAdresse: /Adresse\s+([^\n]+)\s+Municipalité\s+([^\n]+)\s+Code postal\s+([^\n]+)\s+Province\s+([^\n]+)\s+Pays\s+([^\n]+)/i,
  auteurSignature: /Signé par\s+([^\n]+)/i,
  funeraireNom: /Nom de la maison funéraire\s+([^\n]+)/i,
  funerairePermis: /Numéro de permis\s+([^\n]+)/i
};

function extractWithRegex(text: string, pattern: RegExp, groupIndices: number[] = [1]): string | string[] | null {
  const m = text.match(pattern);
  if (!m) return null;
  if (groupIndices.length === 1) return m[groupIndices[0]]?.trim() ?? null;
  return groupIndices.map(i => m[i]?.trim() ?? null);
}

const fieldExtractors = {
  dateNaissance: (t: string) => extractWithRegex(t, LABELS.dateNaissance),
  sexe: (t: string) => extractWithRegex(t, LABELS.sexe),
  age: (t: string) => extractWithRegex(t, LABELS.age),
  provinceNaissance: (t: string) => extractWithRegex(t, LABELS.provinceNaissance),
  paysNaissance: (t: string) => extractWithRegex(t, LABELS.paysNaissance),
  numeroAssurance: (t: string) => extractWithRegex(t, LABELS.numeroAssurance),
  numeroFormulaire: (t: string) => extractWithRegex(t, LABELS.numeroFormulaire),
  endroitDeces: (t: string) => extractWithRegex(t, LABELS.endroitDeces),
  codeLieu: (t: string) => extractWithRegex(t, LABELS.codeLieu),
  adresseDeces: (t: string) => {
    const v = extractWithRegex(t, LABELS.adresseDeces, [1,2,3,4,5]) as string[] | null;
    if (!v) return null;
    const [adresse, municipalite, codePostal, province, pays] = v;
    return { adresse, municipalite, codePostal, province, pays };
  },
  pere: (t: string) => extractWithRegex(t, LABELS.pere),
  mere: (t: string) => extractWithRegex(t, LABELS.mere),
  statutCivil: (t: string) => extractWithRegex(t, LABELS.statutCivil),
  nomDefunt: (t: string) => extractWithRegex(t, LABELS.nomDefunt),
  dateHeureDeces: (t: string) => extractWithRegex(t, LABELS.dateHeureDeces),
  causePrincipale: (t: string) => extractWithRegex(t, LABELS.causea),
  autopsie: (t: string) => extractWithRegex(t, LABELS.autopsie),
  auteur: (t: string) => {
    const nom = extractWithRegex(t, LABELS.auteurSignature) ?? extractWithRegex(t, LABELS.auteurNom);
    const qualite = extractWithRegex(t, LABELS.auteurQualite);
    const permis = extractWithRegex(t, LABELS.auteurPermis);
    const adr = extractWithRegex(t, LABELS.auteurAdresse, [1,2,3,4,5]) as string[] | null;
    const adresse = adr ? { adresse: adr[0], municipalite: adr[1], codePostal: adr[2], province: adr[3], pays: adr[4] } : null;
    return { nom, qualite, permis, adresse };
  },
  maisonFuneraire: (t: string) => {
    const nom = extractWithRegex(t, LABELS.funeraireNom);
    const permis = extractWithRegex(t, LABELS.funerairePermis);
    return (nom || permis) ? { nom, permis } : null;
  }
};

interface SP3PdfParserProps {
  disabled?: boolean;
}

export const SP3PdfParser = ({ disabled = false }: SP3PdfParserProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sp3File, setSp3File] = useState<any>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const record = useRecordContext<Funeral>();
  const refresh = useRefresh();

  // Fetch SP3 attachments for this funeral
  const { data: attachments } = useGetList('attachments', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'DESC' },
    filter: { funeral_id: record?.id },
  });

  const handleOpen = () => {
    // Find the SP3 file when opening the dialog
    if (attachments) {
      const sp3Attachment = attachments.find((attachment: any) => 
        attachment.document_type === 'SP3' || 
        (attachment.title && attachment.title.toLowerCase().includes('sp3'))
      );
      setSp3File(sp3Attachment);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setParsedData(null);
    setError(null);
    setSp3File(null);
    setRawText(null);
  };

  // Extract text from PDF using PDF.js
  const extractTextFromPdf = async (pdfUrl: string): Promise<string> => {
    try {
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Load the PDF document
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      let fullText = '';
      
      // Extract text from all pages
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Combine all text items from the page
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + '\n';
      }
      
      console.log('Extracted PDF text:', fullText);
      return fullText;
      
    } catch (err) {
      throw new Error('Failed to extract text from PDF: ' + (err as Error).message);
    }
  };

  // Parse the actual SP3 file using backend service
  const handleParseActualSP3 = async () => {
    if (!sp3File) {
      setError('No SP3 file found for this funeral');
      return;
    }

    console.log('SP3 File object:', sp3File);
    console.log('Attachment ID being sent:', sp3File.id);

    setLoading(true);
    setError(null);
    
    try {
      // Get the authentication token
      const token = await getToken();
      
      // Call the NestJS backend to process the SP3 file
      const nestjsUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
      const url = `${nestjsUrl}sp3/process/attachment/${sp3File.id}`;
      console.log('Calling URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'Backend processing failed');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Backend processing failed');
      }

      // Set the results from backend processing
      setRawText(result.rawText || 'Raw text not available');
      setParsedData(result.extractedData || {});
      
      // Refresh the page to show the updated data
      refresh();
      
    } catch (err) {
      setError('Failed to parse SP3 file: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Process all SP3 files for this funeral
  const handleProcessAllSP3 = async () => {
    if (!record?.id) {
      setError('No funeral ID available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Get the authentication token
      const token = await getToken();
      
      // Call the NestJS backend to process all SP3 files for this funeral
      const nestjsUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
      const response = await fetch(`${nestjsUrl}sp3/process/funeral/${record.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'Backend processing failed');
      }

      const results = await response.json();
      
      // Find the first successful result
      const successfulResult = results.find((r: any) => r.success);
      
      if (!successfulResult) {
        const errors = results.map((r: any) => r.error).filter(Boolean);
        throw new Error(errors.join(', ') || 'All SP3 processing failed');
      }

      // Set the results from the successful processing
      setRawText(successfulResult.rawText || 'Raw text not available');
      setParsedData(successfulResult.extractedData || {});
      
      // Refresh the page to show the updated data
      refresh();
      
      // Show success message if multiple files were processed
      if (results.length > 1) {
        const successCount = results.filter((r: any) => r.success).length;
        alert(`Successfully processed ${successCount} of ${results.length} SP3 files`);
      }
      
    } catch (err) {
      setError('Failed to process SP3 files: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Test parsing with sample data
  const handleTestParsing = () => {
    setLoading(true);
    setError(null);
    
    const sampleSP3Text = `
    Nom et prénom de la personne décédée: Jean-Pierre Dupont
    Date de naissance 1955-03-15
    Sexe Masculin
    Âge 68 a
    Province de naissance Québec
    Pays de naissance Canada
    No d'assurance maladie DUPJ55031502
    No de formulaire SP3-2023-001234
    Date et heure du décès 2023-11-15 14:30:00
    Endroit où le décès est survenu Hôpital général
    Nom et prénom du père Pierre Dupont
    Nom et prénom de la mère Marie Tremblay
    Statut Marié
    a) due à Arrêt cardiaque
    Y a-t-il eu autopsie? Non
    Nom de la maison funéraire Paperman & Sons
    Numéro de permis PF-2023-456
    `;

    setTimeout(() => {
      try {
        const extractedData: Record<string, any> = {};
        
        for (const [key, extractor] of Object.entries(fieldExtractors)) {
          extractedData[key] = extractor(sampleSP3Text);
        }
        
        setParsedData(extractedData);
        setLoading(false);
      } catch (err) {
        setError('Failed to parse SP3 data: ' + (err as Error).message);
        setLoading(false);
      }
    }, 1000);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<PictureAsPdfIcon />}
        variant="outlined"
        size="small"
        disabled={disabled}
      >
        Test SP3 PDF Parser
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          SP3 PDF Parser Test
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              This interface demonstrates the new regex-based SP3 PDF parsing approach.
            </Typography>

            {/* SP3 File Status */}
            {sp3File ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                SP3 file found: <strong>{sp3File.title}</strong>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No SP3 file found for this funeral. You can test with sample data below.
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              {sp3File && (
                <Button 
                  variant="contained" 
                  onClick={handleParseActualSP3}
                  disabled={loading}
                  color="primary"
                >
                  {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  Parse This SP3 File
                </Button>
              )}
              
              {record?.id && (
                <Button 
                  variant="contained" 
                  onClick={handleProcessAllSP3}
                  disabled={loading}
                  color="secondary"
                >
                  {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  Process All SP3 Files
                </Button>
              )}
              
              <Button 
                variant="outlined" 
                onClick={handleTestParsing}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Test with Sample Data
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {rawText && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Raw Extracted Text:
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: 200, 
                  overflow: 'auto', 
                  border: 1, 
                  borderColor: 'grey.300', 
                  p: 2, 
                  backgroundColor: 'grey.50',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {rawText}
              </Box>
            </Box>
          )}

          {parsedData && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Extracted Data:
              </Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(parsedData).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          fontWeight: 'bold',
                          width: '25%',
                          verticalAlign: 'top'
                        }}
                      >
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem'
                        }}
                      >
                        {formatValue(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};